const scheduledSaves = new Map();
const inFlightSaves = new Set();
const saveTails = new Map();
const saveErrors = new Map();

const rememberInFlight = (promise) => {
  inFlightSaves.add(promise);
  const forget = () => inFlightSaves.delete(promise);
  promise.then(forget, forget);
  // UI event handlers often do not await debounced saves. Mark the rejection
  // handled here while preserving it for callers and the render flush.
  promise.catch(() => undefined);
  return promise;
};

/**
 * Run saves for the same field sequentially so an older HTTP response cannot
 * arrive after a newer one and leave the server with stale project data.
 */
export const runProjectSave = (key, task) => {
  const previous = saveTails.get(key) || Promise.resolve();
  const operation = previous.catch(() => undefined).then(task);
  saveTails.set(key, operation);
  const forgetTail = () => {
    if (saveTails.get(key) === operation) saveTails.delete(key);
  };
  operation.then(forgetTail, forgetTail);
  operation.then(
    () => saveErrors.delete(key),
    (error) => saveErrors.set(key, error),
  );
  return rememberInFlight(operation);
};

/**
 * Debounce a save without tying its timer to a component's lifetime. The task
 * captures the newest value, survives the editor/render route change, and can
 * be flushed before the render page opens.
 */
export const scheduleProjectSave = (key, task, delay = 0) => {
  const existing = scheduledSaves.get(key);
  if (existing) {
    clearTimeout(existing.timer);
    existing.task = task;
    existing.timer = setTimeout(existing.run, delay);
    return existing.promise;
  }

  let resolveScheduled;
  let rejectScheduled;
  const entry = {
    key,
    task,
    timer: null,
    running: false,
    promise: new Promise((resolve, reject) => {
      resolveScheduled = resolve;
      rejectScheduled = reject;
    }),
    run: null,
  };

  entry.run = () => {
    if (entry.running) return entry.promise;
    entry.running = true;
    clearTimeout(entry.timer);
    scheduledSaves.delete(key);
    runProjectSave(key, () => entry.task()).then(resolveScheduled, rejectScheduled);
    return entry.promise;
  };

  entry.timer = setTimeout(entry.run, delay);
  scheduledSaves.set(key, entry);
  return entry.promise;
};

export const hasPendingProjectSaves = () =>
  scheduledSaves.size > 0 || inFlightSaves.size > 0;

/** Flush every debounce and wait until all queued/in-flight writes settle. */
export const flushPendingProjectSaves = async () => {
  while (scheduledSaves.size || inFlightSaves.size) {
    const scheduled = [...scheduledSaves.values()];
    scheduled.forEach((entry) => entry.run());
    const pending = [...inFlightSaves, ...scheduled.map((entry) => entry.promise)];
    if (pending.length) await Promise.allSettled(pending);
  }
  if (saveErrors.size) {
    const error = saveErrors.values().next().value;
    saveErrors.clear();
    throw error;
  }
};

export const resetProjectSaveCoordinatorForTests = () => {
  scheduledSaves.forEach((entry) => clearTimeout(entry.timer));
  scheduledSaves.clear();
  inFlightSaves.clear();
  saveTails.clear();
  saveErrors.clear();
};
